import { useId } from 'react';
import { inputCls, labelCls } from '@/components/admin/FormComponents';
import {
  PARAMETER_TYPES,
  PARAMETER_TYPE_LABELS,
  PARAMETER_TYPE_CONFIG_FIELDS,
  CONFIG_FIELD_LABELS,
  type CccdParameterType,
  type ConfigField,
} from '@/lib/cccdScoringParameters';

export interface ParameterConfigValues {
  name: string;
  parameterType: CccdParameterType;
  unitOfMeasure: string;
  maxScore: string;
  targetHeight: string;
  maxError: string;
  perfectBand: string;
  minTime: string;
  maxTime: string;
  tolerance: string;
  maxValue: string;
}

export const EMPTY_PARAMETER_CONFIG: ParameterConfigValues = {
  name: '',
  parameterType: 'numeric_value',
  unitOfMeasure: '',
  maxScore: '',
  targetHeight: '',
  maxError: '',
  perfectBand: '',
  minTime: '',
  maxTime: '',
  tolerance: '',
  maxValue: '',
};

// Convierte los valores de texto del formulario a lo que espera la API —
// solo los campos aplicables al parameterType elegido se envían como número,
// el resto queda null (nunca se manda "" a un campo Float?).
export function toParameterPayload(values: ParameterConfigValues) {
  const fields = PARAMETER_TYPE_CONFIG_FIELDS[values.parameterType];
  const num = (field: ConfigField) => (fields.includes(field) && values[field] !== '' ? Number(values[field]) : null);
  return {
    name: values.name.trim(),
    parameterType: values.parameterType,
    unitOfMeasure: values.unitOfMeasure.trim() || null,
    maxScore: num('maxScore'),
    targetHeight: num('targetHeight'),
    maxError: num('maxError'),
    perfectBand: num('perfectBand'),
    minTime: num('minTime'),
    maxTime: num('maxTime'),
    tolerance: num('tolerance'),
    maxValue: num('maxValue'),
  };
}

// Formulario compartido (nombre + tipo + campos condicionales según tipo)
// usado tanto por parámetros de evento (ScoringParametersSection) como por
// plantillas globales (ScoringTemplatesPage) — mismos 8 campos planos,
// distinto destino al guardar.
export function ParameterConfigFields({
  values,
  onChange,
}: {
  values: ParameterConfigValues;
  onChange: (next: ParameterConfigValues) => void;
}) {
  const idPrefix = useId();
  const set = <K extends keyof ParameterConfigValues>(key: K, v: ParameterConfigValues[K]) => onChange({ ...values, [key]: v });
  const activeFields = PARAMETER_TYPE_CONFIG_FIELDS[values.parameterType];

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-name`} className={labelCls}>Nombre *</label>
          <input id={`${idPrefix}-name`} className={inputCls} value={values.name} onChange={(e) => set('name', e.target.value)} placeholder="Ej. Altura alcanzada" />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-type`} className={labelCls}>Tipo de parámetro *</label>
          <select
            id={`${idPrefix}-type`}
            className={inputCls}
            value={values.parameterType}
            onChange={(e) => set('parameterType', e.target.value as CccdParameterType)}
          >
            {PARAMETER_TYPES.map((t) => (
              <option key={t} value={t}>{PARAMETER_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`${idPrefix}-unit`} className={labelCls}>Unidad de medida</label>
          <input id={`${idPrefix}-unit`} className={inputCls} value={values.unitOfMeasure} onChange={(e) => set('unitOfMeasure', e.target.value)} placeholder="m, s..." />
        </div>
      </div>
      {activeFields.length > 0 && (
        <div className="border-t border-gray-100 pt-4 dark:border-gray-800">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Configuración de puntaje</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {activeFields.map((field) => (
              <div key={field}>
                <label htmlFor={`${idPrefix}-${field}`} className={labelCls}>{CONFIG_FIELD_LABELS[field]}</label>
                <input
                  id={`${idPrefix}-${field}`}
                  type="number"
                  step="any"
                  inputMode="decimal"
                  className={inputCls}
                  value={values[field]}
                  onChange={(e) => set(field, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
